import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Clock, Pencil, Copy, Globe } from 'lucide-react';
import { formatClipboardCoords } from '@/lib/coords';

const MENU_WIDTH = 220;
const MENU_HEIGHT_ESTIMATE = 180;

export default function POIContextMenu({
  poi,
  x,
  y,
  onClose,
  onViewVisits,
  onEdit,
  onCopyCoords,
}) {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const isAdmin = useSelector((state) => state.session.user?.administrator);

  const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const clampedY = Math.min(y, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') handleClose(); }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) handleClose();
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [handleClose]);

  if (!poi) return null;

  const isGlobal = poi.organization_id === null;
  // Global POIs are editable only by sultans (mirrors POIForm's isReadOnly).
  const canEdit = !(isGlobal && !isAdmin);

  const coords = { lat: poi.latitude, lng: poi.longitude };

  const actions = [
    {
      icon: Clock,
      label: t('pois.viewVisits', 'View visits'),
      onClick: () => { onViewVisits?.(poi); handleClose(); },
    },
    {
      icon: Pencil,
      label: canEdit
        ? t('pois.edit', 'Edit POI')
        : t('pois.viewDetails', 'View details'),
      onClick: () => { onEdit?.(poi); handleClose(); },
    },
    {
      icon: Copy,
      label: t('map.copyCoordinates', 'Copy coordinates'),
      onClick: () => { onCopyCoords?.(coords); handleClose(); },
    },
  ];

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[220px] bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl shadow-xl dark:shadow-black/50 border border-slate-200/60 dark:border-slate-700/60 py-1 overflow-hidden"
      style={{ left: clampedX, top: clampedY }}
    >
      <div className="px-3 py-2 border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: poi.color || '#3b82f6' }}
          />
          <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate text-start">
            {poi.name}
          </p>
          {isGlobal && <Globe size={11} className="text-blue-500 flex-shrink-0" />}
        </div>
        <p className="mt-0.5 text-[10px] font-mono text-slate-400 dark:text-slate-500 truncate text-start" dir="ltr">
          {formatClipboardCoords(coords)}
        </p>
      </div>
      <div className="py-1">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-start text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
          >
            <action.icon size={15} className="flex-shrink-0 text-slate-500 dark:text-slate-400" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}
